// ============================================================================
// REGROUP TRANSACTION SERVICE
// ============================================================================

import { get, post, put, patch, del } from '../client/axiosClient';
import { regroupEndpoints } from '../endpoints/regroup.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { ApiResponse, PaginatedResponse, ID } from '../types';
import { transformPaginatedResponse } from '../types/common.types';
import type {
  RegroupTransaction,
  CreateRegroupPayload,
  UpdateRegroupPayload,
  RegroupFilterParams,
  RegroupSearchResult,
  RegroupCalculatePayload,
  RegroupCalculateResult,
} from '../types/regroup.types';

class RegroupService {
  async getAll(params?: RegroupFilterParams): Promise<PaginatedResponse<RegroupTransaction>> {
    const endpoint = params
      ? buildEndpointWithQuery(regroupEndpoints.list(), params)
      : regroupEndpoints.list();
    const response = await get(endpoint);
    return transformPaginatedResponse<RegroupTransaction>(response.data);
  }

  async getById(id: ID): Promise<ApiResponse<RegroupTransaction>> {
    const response = await get<ApiResponse<RegroupTransaction>>(regroupEndpoints.getById(id));
    return response.data;
  }

  async search(searchTerm: string): Promise<ApiResponse<RegroupSearchResult>> {
    const response = await get<ApiResponse<RegroupSearchResult>>(regroupEndpoints.search(searchTerm));
    return response.data;
  }

  async calculate(payload: RegroupCalculatePayload): Promise<ApiResponse<RegroupCalculateResult>> {
    const response = await post<ApiResponse<RegroupCalculateResult>>(regroupEndpoints.calculate(), payload);
    return response.data;
  }

  async create(payload: CreateRegroupPayload): Promise<ApiResponse<RegroupTransaction>> {
    const response = await post<ApiResponse<RegroupTransaction>>(regroupEndpoints.create(), payload);
    return response.data;
  }

  async update(id: ID, payload: UpdateRegroupPayload): Promise<ApiResponse<RegroupTransaction>> {
    const response = await put<ApiResponse<RegroupTransaction>>(regroupEndpoints.update(id), payload);
    return response.data;
  }

  async submit(id: ID): Promise<ApiResponse<RegroupTransaction>> {
    const response = await patch<ApiResponse<RegroupTransaction>>(regroupEndpoints.submit(id), {});
    return response.data;
  }

  async delete(id: ID): Promise<ApiResponse<void>> {
    const response = await del<ApiResponse<void>>(regroupEndpoints.delete(id));
    return response.data;
  }
}

export const regroupService = new RegroupService();
export default regroupService;
